/**
 * Утилиты для работы с waveform и аудиозаписью.
 */

/**
 * Возвращает предпочтительный MIME-тип для MediaRecorder,
 * поддерживаемый текущим браузером.
 * @returns {string} MIME-тип (audio/webm, audio/mp4, или пустая строка)
 */
export function getSupportedAudioMimeType() {
    const types = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/mp4',
        'audio/mpeg',
    ];
    for (const type of types) {
        if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type)) {
            return type;
        }
    }
    return '';
}

/**
 * Расширение файла для MIME-типа записи.
 * @param {string} mimeType
 * @returns {string}
 */
export function getAudioExtension(mimeType) {
    if (mimeType.includes('webm')) return 'webm';
    if (mimeType.includes('ogg')) return 'ogg';
    if (mimeType.includes('mp4')) return 'm4a';
    if (mimeType.includes('mpeg')) return 'mp3';
    return 'webm';
}

/**
 * Генерирует массив амплитуд для waveform из AudioBuffer.
 *
 * Алгоритм: разбивает PCM-данные на N сегментов (bars),
 * для каждого берёт максимальную амплитуду, нормализует к 0..1.
 *
 * @param {AudioBuffer} audioBuffer — декодированный аудио-буфер
 * @param {number} [bars=50] — количество столбиков waveform
 * @returns {number[]} массив амплитуд от 0 до 1
 */
export function generateWaveformFromBuffer(audioBuffer, bars = 50) {
    const channelData = audioBuffer.getChannelData(0);
    const samplesPerBar = Math.floor(channelData.length / bars);
    if (samplesPerBar === 0) return new Array(bars).fill(0);

    const amplitudes = [];
    let maxAmplitude = 0;

    for (let i = 0; i < bars; i++) {
        const start = i * samplesPerBar;
        const end = Math.min(start + samplesPerBar, channelData.length);
        let peak = 0;
        for (let j = start; j < end; j++) {
            const abs = Math.abs(channelData[j]);
            if (abs > peak) peak = abs;
        }
        amplitudes.push(peak);
        if (peak > maxAmplitude) maxAmplitude = peak;
    }

    // Нормализуем к 0..1, избегая деления на 0
    const normalizer = maxAmplitude > 0 ? maxAmplitude : 1;
    return amplitudes.map(a => a / normalizer);
}

/**
 * Декодирует Blob/File в AudioBuffer и генерирует waveform.
 *
 * @param {Blob|File} blob — аудио-данные
 * @param {number} [bars=50] — количество столбиков
 * @returns {Promise<number[]>} массив амплитуд от 0 до 1
 */
export async function generateWaveformFromBlob(blob, bars = 50) {
    const arrayBuffer = await blob.arrayBuffer();
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const audioCtx = new AudioCtx();
    try {
        const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
        return generateWaveformFromBuffer(audioBuffer, bars);
    } finally {
        audioCtx.close();
    }
}

/**
 * Форматирует секунды в mm:ss.
 * @param {number} seconds
 * @returns {string}
 */
export function formatDuration(seconds) {
    if (isNaN(seconds) || seconds < 0) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Кодирует массив PCM-чанков (Float32Array) напрямую в WAV Blob (16-bit, mono).
 *
 * Используется VoiceRecorder для записи через ScriptProcessorNode —
 * PCM-данные собираются напрямую, минуя MediaRecorder/webm/opus/decodeAudioData.
 * Это полностью решает проблему замедленного воспроизведения.
 *
 * @param {Float32Array[]} pcmChunks — массив PCM-чанков (float [-1,1])
 * @param {number} sampleRate — частота дискретизации (из AudioContext.sampleRate)
 * @param {number} [actualDuration] — реальная длительность в секундах (из таймера).
 *        ScriptProcessorNode на некоторых системах отдаёт сэмплы с удвоенной
 *        скоростью; если actualDuration передан, проверяем и исправляем.
 * @param {Object} [options] — опции кодирования
 * @param {number} [options.targetSampleRate=16000] — целевая частота для даунсемплинга
 * @param {boolean} [options.normalize=true] — peak-нормализация к -3dB
 * @param {string} [options.decimationMode='averaging'] — 'averaging' (anti-alias, сглаживает)
 *        или 'simple' (каждый K-й сэмпл, сохраняет пики/амплитуду)
 * @returns {Blob} WAV-файл (audio/wav)
 */
export function encodePcmToWav(pcmChunks, sampleRate, actualDuration, options = {}) {
    const {
        targetSampleRate = 16000,
        normalize = true,
        decimationMode = 'averaging',
    } = options;

    const numChannels = 1;
    const bytesPerSample = 2; // 16-bit
    const blockAlign = numChannels * bytesPerSample;

    // Считаем общее количество сэмплов
    let totalSamples = 0;
    for (const chunk of pcmChunks) {
        totalSamples += chunk.length;
    }

    // Проверяем, не удвоил ли ScriptProcessorNode частоту сэмплов.
    // Если реальная частота (totalSamples / duration) ≈ 2× sampleRate,
    // даунсемплим — берём каждый второй сэмпл.
    let needsDownsample = false;
    if (actualDuration && actualDuration > 0) {
        const effectiveRate = totalSamples / actualDuration;
        const ratio = effectiveRate / sampleRate;
        if (ratio > 1.5) {
            console.warn('[encodePcmToWav] Sample rate mismatch detected:', {
                expected: sampleRate,
                effective: Math.round(effectiveRate),
                ratio: ratio.toFixed(2),
                action: 'downsampling by ' + Math.round(ratio)
            });
            needsDownsample = true;
        }
    }

    // Даунсемплинг: берём каждый второй сэмпл (для ScriptProcessorNode-бага)
    let processedChunks = pcmChunks;
    if (needsDownsample) {
        processedChunks = [];
        for (const chunk of pcmChunks) {
            const half = new Float32Array(Math.floor(chunk.length / 2));
            for (let i = 0, j = 0; i < chunk.length; i += 2, j++) {
                half[j] = chunk[i];
            }
            processedChunks.push(half);
        }
        totalSamples = Math.floor(totalSamples / 2);
    }

    // Даунсемплинг до целевой частоты (для голоса 16 кГц — оптимально)
    const effectiveRate = needsDownsample ? sampleRate / 2 : sampleRate;
    if (targetSampleRate < effectiveRate) {
        const ratio = effectiveRate / targetSampleRate;

        // Собираем все чанки в один плоский массив
        let flatLength = 0;
        for (const chunk of processedChunks) {
            flatLength += chunk.length;
        }
        const flat = new Float32Array(flatLength);
        let offset = 0;
        for (const chunk of processedChunks) {
            flat.set(chunk, offset);
            offset += chunk.length;
        }

        const outputLength = Math.floor(flatLength / ratio);
        const downsampled = new Float32Array(outputLength);

        if (decimationMode === 'simple') {
            // Simple decimation: берём каждый K-й сэмпл.
            // Сохраняет амплитуду и атаки (не сглаживает),
            // но без anti-aliasing — для голоса приемлемо.
            for (let i = 0; i < outputLength; i++) {
                downsampled[i] = flat[Math.floor(i * ratio)] || 0;
            }
        } else {
            // Averaging decimation: для каждого выходного сэмпла усредняем
            // соответствующий диапазон входных сэмплов (анти-алиасинг).
            // Сглаживает пики — делает звук тише.
            for (let i = 0; i < outputLength; i++) {
                const startIdx = Math.floor(i * ratio);
                const endIdx = Math.floor(Math.min((i + 1) * ratio, flatLength));
                let sum = 0;
                const count = endIdx - startIdx;
                for (let j = startIdx; j < endIdx; j++) {
                    sum += flat[j];
                }
                downsampled[i] = count > 0 ? sum / count : 0;
            }
        }

        processedChunks = [downsampled];
        totalSamples = outputLength;
    }

    // Peak-нормализация: находим пик и масштабируем до -3dB (0.707)
    // Это делает тихие записи громче без клиппирования
    if (normalize) {
        let peak = 0;
        for (const chunk of processedChunks) {
            for (let i = 0; i < chunk.length; i++) {
                const abs = Math.abs(chunk[i]);
                if (abs > peak) peak = abs;
            }
        }
        if (peak > 0.001 && peak < 1) {
            const targetPeak = 0.707; // -3 dBFS
            const gain = targetPeak / peak;
            processedChunks = processedChunks.map(chunk => {
                const scaled = new Float32Array(chunk.length);
                for (let i = 0; i < chunk.length; i++) {
                    scaled[i] = Math.max(-1, Math.min(1, chunk[i] * gain));
                }
                return scaled;
            });
        } else if (peak >= 1) {
            // Клиппинг — не нормализуем, иначе будут искажения
            console.warn('[encodePcmToWav] Peak >= 1.0, skipping normalization');
        }
    }

    const byteRate = targetSampleRate * blockAlign;
    const dataSize = totalSamples * blockAlign;
    const bufferSize = 44 + dataSize;

    const buffer = new ArrayBuffer(bufferSize);
    const view = new DataView(buffer);

    // === WAV заголовок (44 байта) ===
    writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    writeString(view, 8, 'WAVE');

    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);              // PCM
    view.setUint16(22, numChannels, true);
    view.setUint32(24, targetSampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, 16, true);             // bits per sample

    writeString(view, 36, 'data');
    view.setUint32(40, dataSize, true);

    // === PCM данные (16-bit little-endian) ===
    let pcmOffset = 44;
    for (const chunk of processedChunks) {
        for (let i = 0; i < chunk.length; i++) {
            let sample = chunk[i];
            // Clipping + конвертация float [-1,1] → int16
            sample = Math.max(-1, Math.min(1, sample));
            view.setInt16(pcmOffset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
            pcmOffset += 2;
        }
    }

    return new Blob([buffer], { type: 'audio/wav' });
}

/**
 * Кодирует AudioBuffer в WAV Blob (16-bit PCM, mono).
 *
 * Решает проблему замедленного воспроизведения: MediaRecorder пишет
 * webm/opus с Duration: infinity, из-за чего <audio> играет медленно.
 * WAV не имеет этой проблемы — фиксированная длительность в заголовке.
 *
 * @param {AudioBuffer} audioBuffer — декодированный аудио-буфер
 * @returns {Blob} WAV-файл (audio/wav)
 */
export function audioBufferToWav(audioBuffer) {
    const numChannels = 1; // моно для голосовых
    const sampleRate = audioBuffer.sampleRate;
    const numFrames = audioBuffer.length;

    // Берём первый канал (если stereo — усредняем до моно)
    const channel0 = audioBuffer.getChannelData(0);
    const channel1 = audioBuffer.numberOfChannels > 1
        ? audioBuffer.getChannelData(1)
        : null;

    const bytesPerSample = 2; // 16-bit
    const blockAlign = numChannels * bytesPerSample;
    const byteRate = sampleRate * blockAlign;
    const dataSize = numFrames * blockAlign;
    const bufferSize = 44 + dataSize; // 44 байта заголовок + данные

    const buffer = new ArrayBuffer(bufferSize);
    const view = new DataView(buffer);

    // === WAV заголовок (44 байта) ===
    // RIFF chunk
    writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true); // file size - 8
    writeString(view, 8, 'WAVE');

    // fmt chunk
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);           // chunk size
    view.setUint16(20, 1, true);            // audio format (PCM)
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, 16, true);           // bits per sample

    // data chunk
    writeString(view, 36, 'data');
    view.setUint32(40, dataSize, true);

    // === PCM данные (16-bit little-endian) ===
    let offset = 44;
    for (let i = 0; i < numFrames; i++) {
        let sample = channel0[i];
        if (channel1) {
            sample = (sample + channel1[i]) / 2; // усреднение stereo → mono
        }
        // Clipping + конвертация float [-1,1] → int16 [-32768, 32767]
        sample = Math.max(-1, Math.min(1, sample));
        view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
        offset += 2;
    }

    return new Blob([buffer], { type: 'audio/wav' });
}

/**
 * Декодирует произвольный аудио Blob (webm, mp4, ...) в AudioBuffer.
 * Использует 48000 Гц для соответствия записи MediaRecorder (opus).
 * @param {Blob} blob
 * @returns {Promise<AudioBuffer>}
 */
export async function decodeBlobToAudioBuffer(blob) {
    const arrayBuffer = await blob.arrayBuffer();
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const audioCtx = new AudioCtx({ sampleRate: 48000 });
    try {
        return await audioCtx.decodeAudioData(arrayBuffer);
    } finally {
        audioCtx.close();
    }
}

/**
 * Записывает строку в DataView (ASCII).
 * @param {DataView} view
 * @param {number} offset
 * @param {string} str
 */
function writeString(view, offset, str) {
    for (let i = 0; i < str.length; i++) {
        view.setUint8(offset + i, str.charCodeAt(i));
    }
}
